import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import MultipleTailNumberSearch from '@/features/search/MultipleTailNumberSearch';

const ParsePilot = () => {
  const [activeTab, setActiveTab] = useState('part135');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Part 135 State
  const [allData, setAllData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [expandedHolders, setExpandedHolders] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // Charter State
  const [charterCompanies, setCharterCompanies] = useState([]);
  const [filteredCharterCompanies, setFilteredCharterCompanies] = useState([]);
  const [charterSearchInput, setCharterSearchInput] = useState('');
  const [charterLoading, setCharterLoading] = useState(false);
  const [charterError, setCharterError] = useState('');
  const [certFilter, setCertFilter] = useState('');
  const [minScoreFilter, setMinScoreFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Load Charter data when charter tab is activated
  useEffect(() => {
    if (activeTab === 'charter' && charterCompanies.length === 0) {
      loadCharterCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // API Functions
  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const operators = await apiClient.getOperators(0, 1000);
      setAllData(operators);
      setCurrentData(operators);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
      setError('Error loading data: ' + error.message);
    }
  };

  const performSearch = async () => {
    const query = searchInput.trim();

    if (!query) {
      setCurrentData(allData);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Use backend search with name filter
      const results = await apiClient.getOperators(0, 1000, { name: query });
      setCurrentData(results);

      setLoading(false);
    } catch (error) {
      console.error('Error searching operators:', error);
      setError('Error searching operators: ' + error.message);
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setCurrentData(allData);
  };

  const loadCharterCompanies = async () => {
    try {
      setCharterLoading(true);
      setCharterError('');

      const result = await apiClient.getOperators(0, 1000);
      setCharterCompanies(result);
      setFilteredCharterCompanies(result);

      setCharterLoading(false);
    } catch (error) {
      console.error('Error loading charter operators:', error);
      setCharterLoading(false);
      setCharterError('Error loading charter operators');
    }
  };

  const filterCharterCompanies = async () => {
    const query = charterSearchInput.trim();

    if (!query) {
      setFilteredCharterCompanies(charterCompanies);
      return;
    }

    try {
      setCharterLoading(true);
      setCharterError('');

      // Use backend search with name filter
      const results = await apiClient.getOperators(0, 1000, { name: query });
      setFilteredCharterCompanies(results);

      setCharterLoading(false);
    } catch (error) {
      console.error('Error searching charter operators:', error);
      setCharterError('Error searching charter operators');
      setCharterLoading(false);
    }
  };

  // Tail search functions now handled by MultipleTailNumberSearch component

  // Helper Functions
  const groupData = (data) => {
    const grouped = {};

    data.forEach(record => {
      const holderName = record.name || record.operator_name || 'Unknown';

      if (!grouped[holderName]) {
        grouped[holderName] = {
          name: holderName,
          items: []
        };
      }

      grouped[holderName].items.push(record);
    });

    return grouped;
  };

  const toggleHolder = (holderId) => {
    setExpandedHolders(prev => ({
      ...prev,
      [holderId]: !prev[holderId]
    }));
  };


  // Render Functions
  const renderPart135Section = () => {
    const grouped = groupData(currentData);
    const holderNames = Object.keys(grouped).sort();

    return (
      <div id="part135Section" className="section active">
        <div className="controls">
          <div className="search-box">
            <input
              type="text"
              id="searchInput"
              placeholder="Search Part 135 operators..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && performSearch()}
            />
            <button onClick={performSearch}>Search</button>
            <button id="clearBtn" onClick={clearSearch}>Clear</button>
          </div>
          <div className="stats">
            <span>
              Showing {holderNames.length} certificate holders ({currentData.length} items) of {Object.keys(groupData(allData)).length} total
            </span>
          </div>
        </div>

        {loading && <div className="loading">Loading data...</div>}
        {error && <div className="error">{error}</div>}

        <div className="table-container">
          <table id="dataTable">
            <thead>
              <tr>
                <th>Certificate Holder</th>
                <th>Items Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holderNames.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>
                    No data found
                  </td>
                </tr>
              ) : (
                holderNames.map(holderName => {
                  const holder = grouped[holderName];
                  const holderId = holderName.replace(/[^a-zA-Z0-9]/g, '_');
                  const isExpanded = expandedHolders[holderId];

                  return (
                    <React.Fragment key={holderId}>
                      <tr className="holder-row" onClick={() => toggleHolder(holderId)}>
                        <td>
                          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                          <strong>{holderName}</strong>
                        </td>
                        <td>{holder.items.length} items</td>
                        <td>
                          <button
                            className="view-details-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalData(holder.items[0]);
                              setModalOpen(true);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="nested-container">
                          <td colSpan="3">
                            <div className="nested-content">
                              <table className="aircraft-table">
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {holder.items.map((item, idx) => (
                                    <tr key={idx}>
                                      <td>{item.name || item.operator_name || 'N/A'}</td>
                                      <td>{JSON.stringify(item).substring(0, 50)}...</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCharterSection = () => {
    return (
      <div id="charterSection" className="section">
        <div className="charter-search-container">
          <h2>Charter Operators Directory</h2>
          <p className="charter-subtitle">Browse and search business aviation charter operators</p>

          <div className="charter-filters">
            <div className="charter-search-box">
              <input
                type="text"
                placeholder="Search by company name or location..."
                value={charterSearchInput}
                onChange={(e) => setCharterSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && filterCharterCompanies()}
              />
              <button onClick={filterCharterCompanies}>Search</button>
              <button onClick={() => {
                setCharterSearchInput('');
                setFilteredCharterCompanies(charterCompanies);
              }}>Clear</button>
            </div>

            <div className="filter-controls">
              <label>Certification Filter:</label>
              <select value={certFilter} onChange={(e) => setCertFilter(e.target.value)}>
                <option value="">All Operators</option>
                <option value="argus-platinum">ARGUS Platinum</option>
                <option value="argus-gold">ARGUS Gold</option>
                <option value="argus">Any ARGUS</option>
                <option value="wyvern">Wyvern Certified</option>
                <option value="is-bao">IS-BAO</option>
              </select>

              <label style={{ marginLeft: '15px' }}>Min Score:</label>
              <input
                type="number"
                placeholder="0"
                min="0"
                max="500"
                style={{ width: '80px' }}
                value={minScoreFilter}
                onChange={(e) => setMinScoreFilter(e.target.value)}
              />

              <button style={{ marginLeft: '10px' }}>Apply Filters</button>
              <button onClick={() => {
                setCertFilter('');
                setMinScoreFilter('');
                setFilteredCharterCompanies(charterCompanies);
              }}>Clear</button>
            </div>
          </div>

          {charterLoading && <div className="loading">Loading...</div>}
          {charterError && <div className="error">{charterError}</div>}

          <div className="stats" style={{ margin: '20px 0' }}>
            Showing {filteredCharterCompanies.length} of {charterCompanies.length} charter operators
          </div>

          <div className="charter-list">
            {filteredCharterCompanies.map((company, index) => (
              <div key={index} className="charter-company-card">
                <div className="company-header" onClick={() => setModalData(company)}>
                  <h3>{company.name || company.operator_name || 'Unknown Operator'}</h3>
                </div>
                <div className="company-locations">
                  <span className="location-badge">View Details</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTailSection = () => {
    return (
      <div id="tailSection" className="section">
        <MultipleTailNumberSearch onResults={(results) => {
          // Handle results if needed for additional processing
          console.log('Search results:', results);
        }} />
      </div>
    );
  };

  return (
    <div className="container">
      <header>
        <h1>TrustJet Parse Pilot</h1>
        <p className="subtitle">FAA Part 135 & Charter Operator Lookup</p>
      </header>

      <div className="mode-tabs">
        <button
          className={`mode-tab ${activeTab === 'part135' ? 'active' : ''}`}
          onClick={() => setActiveTab('part135')}
        >
          Part 135 Operators
        </button>
        <button
          className={`mode-tab ${activeTab === 'charter' ? 'active' : ''}`}
          onClick={() => setActiveTab('charter')}
        >
          Charter Lookup
        </button>
        <button
          className={`mode-tab ${activeTab === 'tail' ? 'active' : ''}`}
          onClick={() => setActiveTab('tail')}
        >
          Tail Number Search
        </button>
      </div>

      {activeTab === 'part135' && renderPart135Section()}
      {activeTab === 'charter' && renderCharterSection()}
      {activeTab === 'tail' && renderTailSection()}

      {/* Modal */}
      {modalOpen && (
        <div className="modal" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={() => setModalOpen(false)}>&times;</span>
            <div id="modalBody">
              <div className="detail-section">
                <h2>Details</h2>
                <pre>{JSON.stringify(modalData, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParsePilot;
